import type { DataLayerEventPayloads } from '@/lib/analytics/data-layer';
import { clearSessionJSONItem, getSessionJSONItem, setSessionJSONItem } from '@/lib/utils';

export const PENDING_LOGIN_STORAGE_KEY = 'analytics:pendingLogin';
export const PENDING_LOGIN_COOKIE_NAME = 'cc-pending-login';
export const PENDING_LOGIN_HOLD_FALLBACK_MS = 4000;

export type PendingLoginEvent = 'login' | 'sign_up';

export type PendingLoginCookiePayload = {
    event: PendingLoginEvent;
    method: 'magic_link';
    target: DataLayerEventPayloads['login']['target'];
};

export type PendingLogin = {
    method: DataLayerEventPayloads['login']['method'];
    target: DataLayerEventPayloads['login']['target'];
    email_address?: string;
    event?: PendingLoginEvent;
};

let holdReleased = false;
let sawProfileLoading = false;

function isPendingLoginEvent(value: unknown): value is PendingLoginEvent {
    return value === 'login' || value === 'sign_up';
}

function isPendingLogin(value: unknown): value is PendingLogin {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as PendingLogin;
    const hasValidEvent = candidate.event === undefined || isPendingLoginEvent(candidate.event);
    const hasValidEmail = candidate.email_address === undefined || typeof candidate.email_address === 'string';
    const validMethods = ['email', 'google', 'apple', 'magic_link'];
    return (
        validMethods.includes(candidate.method) &&
        (candidate.target === 'shop' || candidate.target === 'club') &&
        hasValidEvent &&
        hasValidEmail
    );
}

function resetHoldCycle(): void {
    holdReleased = false;
    sawProfileLoading = false;
}

function readPendingLoginCookie(): unknown {
    if (typeof document === 'undefined') {
        return undefined;
    }

    const prefix = `${PENDING_LOGIN_COOKIE_NAME}=`;
    for (const part of document.cookie.split(';')) {
        const trimmed = part.trim();
        if (!trimmed.startsWith(prefix)) {
            continue;
        }
        try {
            return JSON.parse(decodeURIComponent(trimmed.slice(prefix.length)));
        } catch {
            return undefined;
        }
    }
    return undefined;
}

function clearPendingLoginCookie(): void {
    if (typeof document === 'undefined') {
        return;
    }
    document.cookie = `${PENDING_LOGIN_COOKIE_NAME}=; Path=/; Max-Age=0`;
}

export function queuePendingLogin(pendingLogin: PendingLogin): void {
    setSessionJSONItem(PENDING_LOGIN_STORAGE_KEY, pendingLogin);
    resetHoldCycle();
}

export function peekPendingLogin(): PendingLogin | undefined {
    const stored = getSessionJSONItem<unknown>(PENDING_LOGIN_STORAGE_KEY);
    if (isPendingLogin(stored)) {
        return stored;
    }
    const fromCookie = readPendingLoginCookie();
    return isPendingLogin(fromCookie) ? fromCookie : undefined;
}

export function consumePendingLogin(): PendingLogin | undefined {
    const pendingLogin = peekPendingLogin();
    clearSessionJSONItem(PENDING_LOGIN_STORAGE_KEY);
    clearPendingLoginCookie();
    resetHoldCycle();
    return pendingLogin;
}

export function releasePendingLoginHold(): void {
    holdReleased = true;
}

export function getPendingLoginHoldKey(): string | null {
    const pendingLogin = peekPendingLogin();
    if (!pendingLogin) {
        return null;
    }
    return `${pendingLogin.event ?? 'login'}:${pendingLogin.method}:${pendingLogin.target}`;
}

export function shouldHoldAuthLifecycle(input: {
    userType: 'registered' | 'guest' | undefined;
    isProfileLoading: boolean;
    hasAnalyticsProfile: boolean;
}): boolean {
    if (!peekPendingLogin() || input.userType !== 'registered' || holdReleased) {
        return false;
    }

    if (input.hasAnalyticsProfile) {
        holdReleased = true;
        return false;
    }

    if (input.isProfileLoading) {
        sawProfileLoading = true;
        return true;
    }

    if (sawProfileLoading) {
        holdReleased = true;
        return false;
    }

    return true;
}

export function resetPendingLoginForTests(): void {
    clearSessionJSONItem(PENDING_LOGIN_STORAGE_KEY);
    clearPendingLoginCookie();
    resetHoldCycle();
}
